import { Channel } from '@/types/channel.types';
import { EmailTextPhrases, EmailTypeEnum, IScheduledEmailCustomizations } from '@/types/email.types';
import { BrandliveEvent } from '@/types/projects.types';
import { LanguagesAbbr } from '@/types/translation.types';
import { namePlaceholder, regCodePlaceholder } from '../scheduled-emails/event-email';
import { getUrl } from './get-url';
import { sessionNameAndDescription } from './session-name-and-description';
import { projectNameAndDescription } from './project-name-and-description';
import { getReminderWording } from './get-reminder-wording';
import { timeFormatForEmails } from './time-format-for-emails';
import { NameFirstInSubject } from './email-defaults';
import { getProjectStartTime } from '@/utils/get-project-start-time';
import { generateIcsFileContent } from './calendar-invites';

interface IUpdatePlainTextEmailsParams {
  workingProject: BrandliveEvent;
  usePlainTextEmail: boolean;
  sessionUuid?: string;
  channel: Channel;
  publishedUrl: string;
  language?: string;
  customizations: IScheduledEmailCustomizations;
  emailType: EmailTypeEnum;
  deliveryDate?: number;
  showStartTime: boolean;
  showAddCalendar: boolean;
  isAllOnDemand: boolean;
  isOnDemandSession: boolean;
}

const updatePlainTextEmails = async (params: IUpdatePlainTextEmailsParams) => {
  const {
    workingProject,
    usePlainTextEmail,
    sessionUuid,
    channel,
    publishedUrl,
    language,
    customizations,
    emailType,
    deliveryDate,
    showStartTime,
    showAddCalendar,
    isAllOnDemand,
    isOnDemandSession,
  } = params;

  let updatedCustomizations = { ...customizations };

  // create plain text emails for each language:
  // if data.settings?.usePlainTextEmail
  // loop through each language,
  // if plainTextEmail does not exist on updatedCustomizations[language], create one using defaultPlainTextEventEmail
  // will need to make sure the eventUrl and attachments are added to the data too
  // add calendar invite attachments if using plain text email and using calendar

  const startTime = workingProject.sessions
    .map((session) => session.timestamp)
    .filter((timestamp) => timestamp)
    .sort()[0];
  const endTime = workingProject.sessions
    .map((session) => session.end_timestamp)
    .filter((timestamp) => timestamp)
    .sort((a, b) => (b as number) - (a as number))[0];
  const session = sessionUuid ? workingProject.sessions.find((session) => session.uuid === sessionUuid) : undefined;

  const isEventInvite = emailType === EmailTypeEnum.event_invite;

  const languages = Array.from(new Set(workingProject.sessions.map((session) => session.languages).flat()));

  if (usePlainTextEmail) {
    for (const _lang of languages) {
      const lang = _lang as LanguagesAbbr;

      if (!updatedCustomizations?.[lang]) {
        updatedCustomizations = {
          ...updatedCustomizations,
          [lang]: {
            subject: '', // we add this later
            base: '',
            eventUrl: getUrl(channel, workingProject, publishedUrl, lang) + regCodePlaceholder,
            // attachments, // we add this later
            plainTextEmail: '', // we add this later
          },
        };
      } else {
        // always update event url in case it changes
        updatedCustomizations = {
          ...updatedCustomizations,
          [lang]: {
            ...updatedCustomizations?.[lang],
            eventUrl: getUrl(channel, workingProject, publishedUrl, lang) + regCodePlaceholder,
          },
        };
      }

      // @ts-expect-error this key will not exist in v2, typescript is panicing over nothing since this entire block will be skipped
      if (updatedCustomizations?.[lang] && !updatedCustomizations?.[lang]?.plainTextEmail) {
        const emailTranslations = await import(`../../../../../../../i18n/${lang}/email.json`);

        // registration uses personalized greeting, event email does not
        const isRegistrationConfirmationEmail = emailType === EmailTypeEnum.event_registration_email;
        const isSessionEmailType = emailType.includes('session');
        const timezone = workingProject?.registration_settings?.registration_email_timezone ?? 'America/Los_Angeles';

        const textPhrase = (key: keyof Omit<EmailTextPhrases, 'subject'>) => {
          return emailTranslations[key];
        };
        const salutationText = isRegistrationConfirmationEmail
          ? `${textPhrase('salutation2')} ${namePlaceholder}, ${textPhrase('registered')}`
          : `${textPhrase('salutation2')}, ${textPhrase('invited')}`;

        const session = sessionUuid
          ? workingProject.sessions.find((session) => session.uuid === sessionUuid)
          : undefined;

        const { name: eventOrSessionName, description: eventOrSessionDescription } = session
          ? sessionNameAndDescription(session, lang)
          : projectNameAndDescription(workingProject, lang);

        if (isSessionEmailType && session) {
          // session emails don't need to loop over all languges, they only need to use the "language" of the email
          // because each one is created separately
          // create plain text session email

          const salutation = textPhrase('salutation') + ' ' + namePlaceholder;
          const emailSendTime = deliveryDate;
          const oneDay = 24 * 60 * 60 * 1000;
          const daysToStart =
            emailType === EmailTypeEnum.session_reminder && session.timestamp && emailSendTime
              ? Math.floor(session.timestamp.valueOf() / oneDay) - Math.floor(emailSendTime.valueOf() / oneDay)
              : undefined;
          const days = daysToStart ?? 0;
          const reminder = `${eventOrSessionName} ${getReminderWording(emailType, days, textPhrase)}`;
          const start: string | null = session.timestamp
            ? timeFormatForEmails(session.timestamp, lang, timezone)
            : null;
          const sessionStart = !!start && showStartTime ? start : '';
          const seeSessionText = textPhrase(
            emailType === EmailTypeEnum.session_reminder ? 'SessionDetails' : 'WatchSession'
          );

          const plainTextEmail = `${salutation},\n\n${reminder}\n\n${sessionStart}\n\n${eventOrSessionDescription}\n\n${seeSessionText}: session_url`;

          const subject = NameFirstInSubject.includes(emailType)
            ? `${eventOrSessionName} ${emailTranslations[`subject.${emailType}`]}`
            : `${emailTranslations[`subject.${emailType}`]} ${eventOrSessionName}`;

          // @ts-expect-error unreachable with a v2 email
          updatedCustomizations = {
            ...updatedCustomizations,
            [lang]: {
              ...updatedCustomizations?.[lang],
              subject: updatedCustomizations?.[lang]?.subject || subject,
              eventUrl: getUrl(channel, workingProject, publishedUrl, lang) + regCodePlaceholder,
              // attachments,
              plainTextEmail,
            },
          };
        } else {
          // if email type is event invitation, then only do this for lang === language
          // because those emails are created separately for each language
          if (!isEventInvite || (isEventInvite && lang === language)) {
            // create plain text event email
            const salutationSplit = salutationText.split(',');
            const salutation = salutationSplit?.[0] || '';
            const registeredForEvent = (salutationSplit?.[1] || '').trimLeft();

            const buttonTextKey =
              !isRegistrationConfirmationEmail && workingProject?.registration_on ? 'RegisterNow' : 'ExploreEvent';
            const buttonText = emailTranslations[buttonTextKey];

            const eventStart = textPhrase('eventStart');

            const start = getProjectStartTime({ project: workingProject });
            const eventStartTime: string | null = eventStart
              ? timeFormatForEmails(start as number, lang, timezone)
              : null;

            const plainTextEmail = `${salutation},\n\n${registeredForEvent} ${eventOrSessionName}\n\n${eventOrSessionDescription}\n\n${eventStart} ${eventStartTime}\n\n${buttonText}: event_url`;

            const subject = NameFirstInSubject.includes(emailType)
              ? `${eventOrSessionName} ${emailTranslations[`subject.${emailType}`]}`
              : `${emailTranslations[`subject.${emailType}`]} ${eventOrSessionName}`;

            // @ts-expect-error unreachable in v2 email
            updatedCustomizations = {
              ...updatedCustomizations,
              [lang]: {
                ...updatedCustomizations?.[lang],
                subject: updatedCustomizations?.[lang]?.subject || subject,
                eventUrl: getUrl(channel, workingProject, publishedUrl, lang) + regCodePlaceholder,
                // attachments,
                plainTextEmail,
              },
            };
          }
        }
      }
    }
  }

  // handle attachments
  // remove calendar invite attachments if not using plain text email or if not using calendar

  if (isAllOnDemand || isOnDemandSession || !showAddCalendar) {
    // as long as it's not event_invite, we should remove the calendar invite for all languages
    // but if it's event_invite, we should only remove it for the current language
    for (const lang of languages) {
      // @ts-expect-error unreachable in v2 email
      if (updatedCustomizations[lang]?.attachments?.length) {
        if (!isEventInvite || (isEventInvite && lang === language)) {
          updatedCustomizations = {
            ...updatedCustomizations,
            [lang]: {
              ...updatedCustomizations[lang],
              // @ts-expect-error unreachable in v2 email
              attachments: (updatedCustomizations[lang].attachments || []).filter((a) => a.contentId !== 'cal'),
            },
          };
        }
      }
    }
  } else if (usePlainTextEmail && showAddCalendar) {
    // do this for every language to make sure it exists and is up to date with the latest event data/time
    for (const _lang of languages) {
      const lang = _lang as LanguagesAbbr;
      if ((!isEventInvite || (isEventInvite && lang === language)) && updatedCustomizations[lang]) {
        // generate calendar attachment data
        const emailTranslations = await import(`../../../../../../../i18n/${lang}/email.json`);
        const { name, description } = session
          ? sessionNameAndDescription(session, lang)
          : projectNameAndDescription(workingProject, lang);
        const url = getUrl(channel, workingProject, publishedUrl, lang, session?.uuid);
        const icsFileContent = generateIcsFileContent(
          startTime,
          endTime,
          name,
          description,
          url,
          workingProject?.uuid,
          emailTranslations,
          !!workingProject?.registration_settings?.registration_email_settings?.icsSettings?.customDescription?.[lang]
        );

        if (icsFileContent) {
          const { icsFileText, fileName } = icsFileContent;
          // remove existing calendar attachments
          // @ts-expect-error unreachable in v2 email
          const filteredAttachments = (updatedCustomizations[lang]?.attachments || []).filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (a: any) => a.contentId !== 'cal'
          );
          // replace with new calendar attachment
          updatedCustomizations = {
            ...updatedCustomizations,
            [lang]: {
              ...updatedCustomizations[lang],
              attachments: [
                ...filteredAttachments,
                {
                  name: fileName,
                  content: icsFileText,
                  contentId: 'cal',
                  contentType: 'text/calendar; charset=utf-8; method=REQUEST',
                  disposition: 'inline',
                },
              ],
            },
          };
        }
      }
    }
  }

  return updatedCustomizations;
};

export default updatePlainTextEmails;
